import React, { useRef, useMemo, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { createNoise3D } from "simplex-noise";

// Helper function to interpolate colors
const interpolateColor = (
  color1: THREE.Color,
  color2: THREE.Color,
  factor: number
) => {
  const result = new THREE.Color();
  result.r = color1.r + factor * (color2.r - color1.r);
  result.g = color1.g + factor * (color2.g - color1.g);
  result.b = color1.b + factor * (color2.b - color1.b);
  return result;
};

// Function to get color from gradient based on position
const getColorFromGradient = (
  position: THREE.Vector3,
  transitionProgress: number
): THREE.Color => {
  const colorA = new THREE.Color(0x4d91e1); // rgb(77,145,225)
  const colorB = new THREE.Color(0x1c1cc8); // rgb(28,28,200)
  const colorC = new THREE.Color(0x0d0088); // rgb(0,212,255)

  const normalizedX = (position.x + 2) / 4; // Normalize x from [-2, 2] to [0, 1]

  let finalColor;
  if (normalizedX < 0.4) {
    finalColor = interpolateColor(colorA, colorB, normalizedX / 0.4);
  } else {
    finalColor = interpolateColor(colorB, colorC, (normalizedX - 0.4) / 0.6);
  }

  const grayA = new THREE.Color(0x808080); // Medium gray
  const grayB = new THREE.Color(0xb0b0b0); // Lighter gray
  const normalizedY = (position.y + 2) / 4; // Normalize y from [-2, 2] to [0, 1]
  const grayColor = interpolateColor(grayA, grayB, normalizedY);

  return grayColor.lerp(finalColor, transitionProgress);
};

interface SphereOfSpheresProps {
  onTogglePause: () => void;
  isTransforming: boolean;
  volume: number;
  connected: boolean;
}
const SphereOfSpheres: React.FC<SphereOfSpheresProps> = ({
  onTogglePause,
  isTransforming,
  volume,
  connected,
}) => {
  const groupRef = useRef<THREE.Group>(null);
  const noise3D = useMemo(() => createNoise3D(), []);
  const timeRef = useRef(0);
  const prevAudioLevelRef = useRef(0);
  const transitionProgress = useRef(0);
  const currentRotationSpeed = useRef(0.0005);

  // const { camera, raycaster, mouse } = useThree();
  const { camera } = useThree();
  const [rotationSpeed, setRotationSpeed] = useState(0.005);
  const [transformProgress, setTransformProgress] = useState(0);

  const sphereData = useMemo(() => {
    const radius = 2;
    const count = 1000;
    const data = [];

    for (let i = 0; i < count; i++) {
      const phi = Math.acos(-1 + (2 * i) / count);
      const theta = Math.sqrt(count * Math.PI) * phi;
      const x = radius * Math.cos(theta) * Math.sin(phi);
      const y = radius * Math.sin(theta) * Math.sin(phi);
      const z = radius * Math.cos(phi);

      const position = new THREE.Vector3(x, y, z);

      data.push({
        basePosition: position,
        ref: React.createRef<THREE.Mesh>(),
      });
    }
    return data;
  }, []);

  useFrame((state, delta) => {
    // Animate transition
    const targetProgress = connected ? 1 : 0;
    transitionProgress.current +=
      (targetProgress - transitionProgress.current) * 0.05;

    // More aggressive smoothing for a slower, more fluid reaction
    const attackFactor = 0.06; // How quickly the blob reacts to sound
    const decayFactor = 0.03; // How slowly the blob returns to idle
    let smoothedLevel;
    if (volume > prevAudioLevelRef.current) {
      smoothedLevel =
        prevAudioLevelRef.current * (1 - attackFactor) + volume * attackFactor;
    } else {
      smoothedLevel =
        prevAudioLevelRef.current * (1 - decayFactor) + volume * decayFactor;
    }
    prevAudioLevelRef.current = smoothedLevel;

    timeRef.current += delta;

    const noiseScale = 0.5;
    const breathingSpeed = 0.5;

    sphereData.forEach((sphere) => {
      if (sphere.ref.current) {
        const basePosition = sphere.basePosition;
        const time = timeRef.current;

        let newPosition;

        if (isTransforming) {
          // Calculate galaxy disk position
          const distance = basePosition.length();
          const angle = Math.atan2(basePosition.y, basePosition.x);
          const galaxyX = distance * Math.cos(angle);
          const galaxyY = distance * Math.sin(angle) * 0.1; // Flatten the y-axis
          const galaxyZ = basePosition.z * (1 - transformProgress); // Gradually flatten z-axis

          newPosition = new THREE.Vector3(galaxyX, galaxyY, galaxyZ);

          // Apply spiral arms effect
          const armOffset =
            (Math.atan2(newPosition.y, newPosition.x) + transformProgress * 5) %
            (Math.PI * 2);
          const armFactor = (Math.cos(armOffset * 2) + 1) * 0.5;
          newPosition.multiplyScalar(1 + armFactor * 0.2 * transformProgress);
        } else {
          // New logic: Audio controls the magnitude of the noise, not uniform growth
          const noiseValue = noise3D(
            basePosition.x * noiseScale + time * breathingSpeed,
            basePosition.y * noiseScale + time * breathingSpeed,
            basePosition.z * noiseScale + time * breathingSpeed
          );

          // A small, constant wobble for when there's no sound
          const idleWobble = 0.05;
          // The audio level scales the intensity of the noise displacement
          const displacementMagnitude = idleWobble + smoothedLevel * 2.0;

          const displacement = noiseValue * displacementMagnitude;
          const normal = basePosition.clone().normalize();
          newPosition = basePosition
            .clone()
            .add(normal.multiplyScalar(displacement));
        }

        sphere.ref.current.position.copy(newPosition);

        // Update the sphere's scale based on audio level
        const scale = 0.05 + smoothedLevel * 0.03;
        sphere.ref.current.scale.set(scale, scale, scale);

        // Adjust color intensity based on audio level
        const material = sphere.ref.current.material as THREE.MeshBasicMaterial;
        material.color.copy(
          getColorFromGradient(basePosition, transitionProgress.current)
        );
        material.color.multiplyScalar(1 + smoothedLevel * 0.5);
      }
    });

    if (groupRef.current) {
      const targetRotationSpeed = connected ? rotationSpeed : 0.0005;
      currentRotationSpeed.current +=
        (targetRotationSpeed - currentRotationSpeed.current) * 0.1;
      groupRef.current.rotation.y += currentRotationSpeed.current;
    }

    if (isTransforming) {
      // Accelerate rotation
      setRotationSpeed((prev) => Math.min(prev + 0.0001, 0.05));

      // Progress transformation
      setTransformProgress((prev) => Math.min(prev + 0.005, 1));

      // Adjust camera position for a better view of the galaxy
      camera.position.z = 6 + transformProgress * 4;
      camera.position.y = transformProgress * 2;
      camera.lookAt(0, 0, 0);
    }
  });

  // const handleClick = (event: ThreeEvent<MouseEvent>) => {
  //   event.stopPropagation();
  //   onTogglePause();
  // };

  return (
    <group ref={groupRef}>
      {sphereData.map((data: any, i: any) => (
        <mesh key={i} ref={data.ref} position={data.basePosition}>
          <sphereGeometry args={[1, 32, 32]} />
          <meshBasicMaterial transparent={true} opacity={0.8} />
        </mesh>
      ))}
    </group>
  );
};

interface PointCloudProps {
  isTransforming: boolean;
  onTogglePause: () => void;
  volume: number;
  connected: boolean;
}

export const PointCloud: React.FC<PointCloudProps> = ({
  onTogglePause,
  isTransforming,
  volume,
  connected,
}) => {
  // Responsive scaling based on screen size with dynamic updates
  const [scale, setScale] = React.useState(1);

  React.useEffect(() => {
    const updateScale = () => {
      if (typeof window === "undefined") return;
      const width = window.innerWidth;
      if (width < 600) {
        setScale(0.85); // Mobile: 85% size
      } else if (width < 900) {
        setScale(0.9); // Tablet: 90% size
      } else {
        setScale(1); // Desktop: 100% size
      }
    };

    // Set initial scale
    updateScale();

    // Update scale on window resize
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  return (
    <Canvas camera={{ position: [0, 0, 6] }} onClick={() => {}}>
      <color attach="background" args={["#ffffff"]} />
      <ambientLight intensity={0.5} />
      <pointLight position={[10, 10, 10]} intensity={1} />
      <group scale={[scale, scale, scale]}>
        <SphereOfSpheres
          onTogglePause={onTogglePause}
          isTransforming={isTransforming}
          volume={volume}
          connected={connected}
        />
      </group>
      {/* <FlatPlane audioLevel={100} /> */}
    </Canvas>
  );
};
